import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
    ApplicationConfig,
    inject,
    InjectionToken,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
    provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideTransloco, Translation, TranslocoService } from '@jsverse/transloco';
import { LocaleFormatOptions, provideTranslocoLocale } from '@jsverse/transloco-locale';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { lastValueFrom } from 'rxjs';
import config from '../assets/config.json';
import { Environment } from '../environments/environment.model';
import { TranslocoHttpLoader } from '../transloco-loader';
import { appRoutes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { forbiddenInterceptor } from './core/interceptors/forbidden.interceptor';
import { ApiFilterTabsRepository } from './core/services/api-filter-tabs.repository';
import { FILTER_TABS_REPOSITORY } from './core/services/filter-tabs.repository';
import { CustomersEffects } from './features/customers/store/effects/customers.effects';
import { customersFeatureKey, customersReducer } from './features/customers/store/reducer/customers.reducer';
import { UserManagementEffects } from './features/user-management/store/effects/user-management.effects';
import { userManagementReducer } from './features/user-management/store/reducers/user-management.reducer';
import { SUPPORTED_LANGUAGES } from './shared/constants';
import { ScreenService } from './shared/services';
import { LocalStorageService } from './shared/services/localStorage.service';

// Custom injection token for runtime configuration
export const RUNTIME_CONFIG = new InjectionToken<Environment>('Runtime Configuration');

const globalFormatConfig: LocaleFormatOptions = {
    date: { dateStyle: 'short', timeZone: 'cet' },
    currency: {
        minimumFractionDigits: 2,
    },
    percent: {},
    decimal: {
        useGrouping: true,
    },
};

export function appInitializerFactory() {
    return (): Promise<Translation> => {
        const translocoService = inject(TranslocoService);
        const localStorageService = inject(LocalStorageService);
        const lang = localStorageService.getLocale();
        return lastValueFrom(translocoService.load(lang.slice(0, 2)));
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideHttpClient(withInterceptors([authInterceptor, forbiddenInterceptor])),
        provideTranslocoLocale({
            langToLocaleMapping: {
                en: 'en-GB',
                de: 'de-DE',
                es: 'es-ES',
            },
            localeConfig: {
                global: globalFormatConfig,
            },
        }),
        provideTransloco({
            config: {
                availableLangs: SUPPORTED_LANGUAGES,
                defaultLang: config.i18n.fallbackLanguage.slice(0, 2),
                reRenderOnLangChange: true,
                prodMode: true, //disables all console warnings from transloco
            },
            loader: TranslocoHttpLoader,
        }),
        provideTranslocoMessageformat({
            locales: ['de-DE', 'en-US', 'es-ES'],
        }),
        provideRouter(appRoutes),

        // NgRx Store Configuration
        provideStore({
            userManagement: userManagementReducer,
            [customersFeatureKey]: customersReducer,
        }),
        provideEffects([UserManagementEffects, CustomersEffects]),
        provideStoreDevtools({
            maxAge: 25,
            logOnly: false, // Set to true in production
            autoPause: true,
            trace: false,
            traceLimit: 75,
        }),

        // Auth providers are registered in main.ts via provideAuthForConfig(runtimeConfig)
        provideAppInitializer(appInitializerFactory()),
        ScreenService,

        // Filter tabs: backend GridLayoutsV2 (with one-time localStorage migration)
        { provide: FILTER_TABS_REPOSITORY, useExisting: ApiFilterTabsRepository },
    ],
};
